'use client';

import { useState, useCallback } from 'react';
import { getUserActivitiesByCompany } from 'src/api/userActivities';

interface DateRange {
  gte: string;
  lte: string;
}

interface UseGetUserActivitiesExportReturn {
  isLoading: boolean;
  error: string | null;
  executeExport: (
    companyId: string,
    dateRange: DateRange,
    format: string
  ) => Promise<void>;
}

/**
 * Hook for exporting user activities by company with date range filtering
 * @returns {UseGetUserActivitiesExportReturn} Object containing loading state, error state, and export function
 */
const useGetUserActivitiesExport = (): UseGetUserActivitiesExportReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const executeExport = useCallback(
    async (companyId: string, dateRange: DateRange, _format: string) => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await getUserActivitiesByCompany({
          id: companyId,
          dateRange,
        });

        // Handle CSV string response (streaming response)
        if (
          typeof response === 'string' &&
          response.startsWith('Activity Type,')
        ) {
          // Create blob from CSV string
          const blob = new Blob([response], {
            type: 'text/csv;charset=utf-8;',
          });

          // Create download link
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `user-activities-export-${companyId}-${dateRange.gte}-to-${dateRange.lte}.csv`;

          // Trigger download
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } else {
          setError('Unexpected response format from server');
        }
      } catch (err) {
        console.error('❌ Export failed:', err);
        setError(err?.error_message || 'User Activities Export Failed');
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return {
    isLoading,
    error,
    executeExport,
  };
};

export default useGetUserActivitiesExport;
